import { RoundedFilledButton } from '@/components/buttons/Buttons';
import Dialog from '@/components/dialog/Dialog';
import DialogHeader from '@/components/dialog/DialogHeader';
import Spinner from '@/components/utils/Spinner';
import { showNotification, showError } from '@/modules/notification/engine/notification.library';
import { BlockUI } from 'primereact/blockui';
import { Checkbox } from 'primereact/checkbox';
import { ProgressBar } from 'primereact/progressbar';
import { classNames } from 'primereact/utils';
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import styles from './add-card-dialog.module.css';
import PaymentMethodForm, { usePaymentMethodForm } from './forms/PaymentMethodForm';
import { ensureXendit, createMultiUseCardToken } from '@/services/xendit.service';
import { BillingApi } from '../billing.api';
import { useModal } from '@ebay/nice-modal-react';


type Props = {
	onCardAdded: () => void;
};

const DIALOG_STATE = {
	LOADING: 'LOADING',
	ACTIVE: 'ACTIVE',
	UPLOADING: 'UPLOADING',
	ERROR: 'ERROR',
} as const;

type DialogState = (typeof DIALOG_STATE)[keyof typeof DIALOG_STATE];

const AddCardDialog = (props: Props) => {
	const [formBlocked, setFormBlocked] = useState<boolean>(false);
	const [dialogState, setDialogState] = useState<DialogState>(
		DIALOG_STATE.ACTIVE,
	);
	const modal = useModal();
	// Use custom hook to manage payment form
	const paymentForm = usePaymentMethodForm({ requireConsent: true });
	const isAddCardDisabled = !paymentForm.formState.isValid;

	useEffect(() => {
		setDialogState(DIALOG_STATE.LOADING);

		setTimeout(() => {
			setDialogState(DIALOG_STATE.ACTIVE);
		}, 3000);
	}, []);

	const handleSubmit = async () => {
		const isValid = await paymentForm.trigger();

		if (!isValid) {
			return;
		}

		const formData = paymentForm.getValues();
		const [expMonth, expYearShort] = formData.ExpDate.split('/');
		const expYear = `20${expYearShort.trim()}`;

		setFormBlocked(true);
		setDialogState(DIALOG_STATE.ACTIVE);

		try {
			const { PublicKey, XenPlatformAccountId, VerificationAmount } = await BillingApi.getXenditConfig();
			await ensureXendit(PublicKey);

			const token = await createMultiUseCardToken({
				cardNumber: formData.CardNumber.replace(/\s/g, ''),
				expMonth: expMonth.trim(),
				expYear,
				cvn: formData.SecurityCode,
				amount: VerificationAmount,
				onBehalfOf: XenPlatformAccountId,
			});

			const res = await BillingApi.createPaymentMethodChangeRequest({
				OldCardId: undefined,
				CardDetails: {
					TokenId: token.id,
					Brand: token.card_info?.brand,
					MaskedCardNumber: token.masked_card_number,
					ExpMonth: parseInt(expMonth, 10),
					ExpYear: parseInt(expYear, 10),
				},
			});

			showNotification(
				res.Data?.Status === 'Approved'
					? 'Card added successfully.'
					: 'Card submitted. Approve it under the Requests tab to activate it.'
			);
			props.onCardAdded();
			modal.remove();
		} catch (e: any) {
			if (e?.message && !e?.response) {
				showError(e.message);
			}
			setDialogState(DIALOG_STATE.ERROR);
		} finally {
			setFormBlocked(false);
		}
	};

	const handleDismiss = () => {
		modal.remove();
		setDialogState(DIALOG_STATE.ACTIVE);
		showNotification(
			'This payment method is currently not supported by the selected service, please try another payment method',
		);
	};

	const handleProceed = () => {
		setDialogState(DIALOG_STATE.ACTIVE);
	};

	const LoadingContent = () => {
		return (
			<div className="h-full overflow-hidden">
				<div className={styles.loadingContainer}>
					<p className="mt-10 mb-8">Connecting...</p>
					<Spinner />
				</div>
			</div>
		);
	};

	const ErrorContent = () => {
		return (
			<div className="h-full">
				<h2 className={styles.errorTitle}>Declined by bank</h2>
				<p className="mb-6">
					Please try another payment method or check with your bank.
				</p>
				<RoundedFilledButton
					id="switch-payment-method"
					label="Switch Payment Method"
					className={styles.roundedButton}
					onClick={handleProceed}
				/>
				<RoundedFilledButton
					id="dismiss"
					label="Dismiss"
					className={styles.dismissButton}
					onClick={handleDismiss}
				/>
			</div>
		);
	};

	const ActiveContent = () => {
		return (
			<>
				<BlockUI
					blocked={formBlocked}
					template="Adding Card"
					className={styles.formBlock}
				>
					<PaymentMethodForm
						gapSize="wide"
						cvvLabel="Security Code"
						control={paymentForm.control}
					/>

					<div className="flex items-center my-9">
						<Controller
							name="consent"
							control={paymentForm.control}
							render={({ field }) => (
								<Checkbox
									inputId="consentCheck"
									name={field.name}
									onChange={(e) => field.onChange(e.checked || false)}
									onBlur={field.onBlur}
									checked={!!field.value}
									className="mr-4 ml-[10px] shrink-0"
									pt={{
										box: () => ({
											className: classNames(
												styles.checkbox,
												field.value
													? styles.checkboxChecked
													: styles.checkboxUnchecked,
											),
										}),
										icon: () => ({
											className: styles.checkboxIcon,
										}),
									}}
								/>
							)}
						/>
						<label
							htmlFor="consentCheck"
							className="ml-2 text-xs text-black"
						>
							I confirm that the information I have provided
							to Xpress regarding the tax situation of my business
							is correct
						</label>
					</div>

					<RoundedFilledButton
						id="add-card"
						label="Add Card"
						className={styles.roundedButton}
						onClick={handleSubmit}
						disabled={isAddCardDisabled}
					/>
				</BlockUI>
			</>
		);
	};

	return (
		<Dialog
			header={
				dialogState === DIALOG_STATE.ACTIVE ? (
					<BlockUI blocked={formBlocked} className={styles.formBlock}>
						<DialogHeader title="Add credit or debit card" />
					</BlockUI>
				) : undefined
			}
			visible={true}
			onHide={() => {
				modal.remove();
			}}
			className={classNames(styles.container, {
				'md:min-h-[320px]': dialogState === DIALOG_STATE.LOADING,
			})}
		>
			<ProgressBar
				mode="indeterminate"
				color="#0080FF"
				className={classNames(styles.progressBar, {
					hidden: !formBlocked,
				})}
			/>
			{dialogState === DIALOG_STATE.LOADING && <LoadingContent />}
			{dialogState === DIALOG_STATE.ACTIVE && <ActiveContent />}
			{dialogState === DIALOG_STATE.ERROR && <ErrorContent />}
			<p
				className={classNames('text-sm ', {
					'mt-4': dialogState === DIALOG_STATE.ACTIVE,
					'mt-[52px]': dialogState !== DIALOG_STATE.ACTIVE,
					hidden: dialogState === DIALOG_STATE.LOADING,
				})}
			>
				This payment method will be charged when users order rides,
				meals, or other Xpress products on your Xpress for Business
				account.
			</p>
		</Dialog>
	);
};

export default AddCardDialog;
